import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2, Target, Pencil } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOMAIN_LABEL, useGoals, usePillars, type Domain, type Pillar } from "@/lib/plan";
import { currentWeekDates, toDateKey, weekStart } from "@/lib/hub";

export const Route = createFileRoute("/_authenticated/pillars")({
  head: () => ({
    meta: [
      { title: "Pillars & goals — MyPriorities" },
      {
        name: "description",
        content:
          "Set the professional and personal pillars you're building, break them into goals, and see the work and habits driving each one.",
      },
      { property: "og:title", content: "Pillars & goals — MyPriorities" },
      {
        property: "og:description",
        content: "The achievements you're chasing, broken into goals your tasks and habits serve.",
      },
    ],
  }),
  component: Pillars,
});

const emptyPillar = { name: "", domain: "professional" as Domain, note: "" };
const emptyGoal = { name: "", measure: "", timeframe: "" };

const TIMEFRAMES = ["This week", "This month", "This quarter", "This year", "Ongoing"] as const;

function Pillars() {
  const queryClient = useQueryClient();
  const pillarsQuery = usePillars();
  const goalsQuery = useGoals();

  const [pillarDialog, setPillarDialog] = useState<{ id: string | null } | null>(null);
  const [pillarDraft, setPillarDraft] = useState({ ...emptyPillar });
  const [goalDialog, setGoalDialog] = useState<{ pillarId: string; id: string | null } | null>(
    null,
  );
  const [goalDraft, setGoalDraft] = useState({ ...emptyGoal });

  const start = weekStart();
  const end = toDateKey(currentWeekDates()[6]!);

  const tasksQuery = useQuery({
    queryKey: ["tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as {
        id: string;
        goal_id: string | null;
        pillar_id: string | null;
        done: boolean;
        hours_week: number;
      }[];
    },
  });

  const habitsQuery = useQuery({
    queryKey: ["habits"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habits")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as {
        id: string;
        goal_id: string | null;
        pillar_id: string | null;
        target_per_week: number;
      }[];
    },
  });

  const logsQuery = useQuery({
    queryKey: ["habit_logs", start],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_logs")
        .select("*")
        .gte("log_date", start)
        .lte("log_date", end);
      if (error) throw error;
      return data as { habit_id: string; log_date: string }[];
    },
  });

  const pillars = pillarsQuery.data ?? [];
  const goals = goalsQuery.data ?? [];
  const tasks = tasksQuery.data ?? [];
  const habits = habitsQuery.data ?? [];
  const logs = logsQuery.data ?? [];

  const savePillar = useMutation({
    mutationFn: async () => {
      const name = pillarDraft.name.trim();
      if (!name) throw new Error("Give the pillar a name");
      const payload = {
        name,
        domain: pillarDraft.domain,
        note: pillarDraft.note.trim() || null,
      };
      if (pillarDialog?.id) {
        const { error } = await supabase.from("pillars").update(payload).eq("id", pillarDialog.id);
        if (error) throw error;
        return;
      }
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("pillars")
        .insert({ ...payload, user_id: auth.user!.id });
      if (error) throw error;
    },
    onSuccess: () => {
      setPillarDialog(null);
      setPillarDraft({ ...emptyPillar });
      queryClient.invalidateQueries({ queryKey: ["pillars"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save"),
  });

  const deletePillar = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pillars").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pillars"] });
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["habits"] });
      toast.success("Pillar removed — your tasks and habits are still there");
    },
  });

  const saveGoal = useMutation({
    mutationFn: async () => {
      const name = goalDraft.name.trim();
      if (!name) throw new Error("Give the goal a name");
      const payload = {
        name,
        measure: goalDraft.measure.trim() || null,
        timeframe: goalDraft.timeframe || null,
      };
      if (goalDialog?.id) {
        const { error } = await supabase.from("goals").update(payload).eq("id", goalDialog.id);
        if (error) throw error;
        return;
      }
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from("goals").insert({
        ...payload,
        pillar_id: goalDialog!.pillarId,
        user_id: auth.user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setGoalDialog(null);
      setGoalDraft({ ...emptyGoal });
      queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not save"),
  });

  const deleteGoal = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("goals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["habits"] });
    },
  });

  function openNewPillar(domain: Domain) {
    setPillarDraft({ ...emptyPillar, domain });
    setPillarDialog({ id: null });
  }

  function openEditPillar(p: Pillar) {
    setPillarDraft({ name: p.name, domain: p.domain, note: p.note ?? "" });
    setPillarDialog({ id: p.id });
  }

  const domains: Domain[] = ["professional", "personal"];

  return (
    <AppShell
      title="Pillars & goals"
      subtitle="The achievements you're building, and the goals that get you there."
      action={
        <Button onClick={() => openNewPillar("professional")}>
          <Plus className="size-4" /> New pillar
        </Button>
      }
    >
      {pillars.length === 0 ? (
        <div className="panel p-10 text-center">
          <Target className="mx-auto size-6 text-primary" />
          <p className="mt-3 text-sm text-muted-foreground">
            Start with a pillar — a big professional or personal achievement, like “Success with my
            company” or “Be a good partner”. Then break it into goals.
          </p>
        </div>
      ) : null}

      {domains.map((domain) => {
        const list = pillars.filter((p) => p.domain === domain);
        if (list.length === 0 && pillars.length === 0) return null;
        return (
          <section key={domain} className="mt-8 first:mt-0">
            <div className="flex items-center gap-3">
              <h2 className="label-caps">{DOMAIN_LABEL[domain]}</h2>
              <Button variant="ghost" size="sm" onClick={() => openNewPillar(domain)}>
                <Plus className="size-4" /> Add
              </Button>
            </div>

            {list.length === 0 ? (
              <p className="panel mt-3 p-5 text-sm text-muted-foreground">
                No {DOMAIN_LABEL[domain].toLowerCase()} pillars yet.
              </p>
            ) : (
              <div className="mt-3 grid gap-4 lg:grid-cols-2">
                {list.map((p) => {
                  const pillarGoals = goals.filter((g) => g.pillar_id === p.id);
                  return (
                    <div key={p.id} className="panel p-5">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                          {p.note ? (
                            <p className="mt-1 text-sm text-muted-foreground">{p.note}</p>
                          ) : null}
                        </div>
                        <div className="ml-auto flex shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${p.name}`}
                            onClick={() => openEditPillar(p)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${p.name}`}
                            onClick={() => deletePillar.mutate(p.id)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4 space-y-2">
                        {pillarGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No goals yet — add the granular things that add up to this.
                          </p>
                        ) : (
                          pillarGoals.map((g) => {
                            const goalTasks = tasks.filter((t) => t.goal_id === g.id);
                            const doneTasks = goalTasks.filter((t) => t.done).length;
                            const goalHabits = habits.filter((h) => h.goal_id === g.id);
                            const habitTarget = goalHabits.reduce(
                              (s, h) => s + h.target_per_week,
                              0,
                            );
                            const habitDone = goalHabits.reduce(
                              (s, h) =>
                                s +
                                Math.min(
                                  h.target_per_week,
                                  logs.filter((l) => l.habit_id === h.id).length,
                                ),
                              0,
                            );
                            const pct =
                              domain === "personal"
                                ? habitTarget
                                  ? (habitDone / habitTarget) * 100
                                  : 0
                                : goalTasks.length
                                  ? (doneTasks / goalTasks.length) * 100
                                  : 0;
                            return (
                              <div
                                key={g.id}
                                className="rounded-md border border-border bg-surface p-3"
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-medium">{g.name}</p>
                                  {g.timeframe ? (
                                    <Badge variant="secondary">{g.timeframe}</Badge>
                                  ) : null}
                                  <div className="ml-auto flex shrink-0">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Edit ${g.name}`}
                                      onClick={() => {
                                        setGoalDraft({
                                          name: g.name,
                                          measure: g.measure ?? "",
                                          timeframe: g.timeframe ?? "",
                                        });
                                        setGoalDialog({ pillarId: p.id, id: g.id });
                                      }}
                                    >
                                      <Pencil className="size-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Delete ${g.name}`}
                                      onClick={() => deleteGoal.mutate(g.id)}
                                    >
                                      <Trash2 className="size-4" />
                                    </Button>
                                  </div>
                                </div>
                                {g.measure ? (
                                  <p className="mt-1 text-xs text-muted-foreground">{g.measure}</p>
                                ) : null}
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {domain === "personal"
                                    ? `${goalHabits.length} habit${goalHabits.length === 1 ? "" : "s"} · ${habitDone}/${habitTarget} check-offs this week`
                                    : `${goalTasks.length} task${goalTasks.length === 1 ? "" : "s"} · ${doneTasks} done · ${round(
                                        goalTasks
                                          .filter((t) => !t.done)
                                          .reduce((s, t) => s + Number(t.hours_week || 0), 0),
                                      )}h this week`}
                                </p>
                                <Progress value={Math.min(100, pct)} className="mt-2" />
                              </div>
                            );
                          })
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          setGoalDraft({ ...emptyGoal });
                          setGoalDialog({ pillarId: p.id, id: null });
                        }}
                      >
                        <Plus className="size-4" /> Add goal
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      <Dialog open={Boolean(pillarDialog)} onOpenChange={(o) => !o && setPillarDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pillarDialog?.id ? "Edit pillar" : "New pillar"}</DialogTitle>
            <DialogDescription>
              A key achievement you're building towards, professional or personal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pname">Pillar</Label>
              <Input
                id="pname"
                placeholder="e.g. Launch my own business"
                value={pillarDraft.name}
                onChange={(e) => setPillarDraft({ ...pillarDraft, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Side of life</Label>
              <Select
                value={pillarDraft.domain}
                onValueChange={(v) => setPillarDraft({ ...pillarDraft, domain: v as Domain })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional — drives your tasks</SelectItem>
                  <SelectItem value="personal">Personal — drives your habits</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pnote">What success looks like</Label>
              <Textarea
                id="pnote"
                value={pillarDraft.note}
                onChange={(e) => setPillarDraft({ ...pillarDraft, note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPillarDialog(null)}>
              Cancel
            </Button>
            <Button onClick={() => savePillar.mutate()} disabled={savePillar.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(goalDialog)} onOpenChange={(o) => !o && setGoalDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{goalDialog?.id ? "Edit goal" : "New goal"}</DialogTitle>
            <DialogDescription>Something granular that builds up to the pillar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gname">Goal</Label>
              <Input
                id="gname"
                placeholder="e.g. Hit budget for the year"
                value={goalDraft.name}
                onChange={(e) => setGoalDraft({ ...goalDraft, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gmeasure">How you'll measure it</Label>
              <Input
                id="gmeasure"
                placeholder="e.g. £2.4m revenue"
                value={goalDraft.measure}
                onChange={(e) => setGoalDraft({ ...goalDraft, measure: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Timeframe</Label>
              <Select
                value={goalDraft.timeframe || "none"}
                onValueChange={(v) =>
                  setGoalDraft({ ...goalDraft, timeframe: v === "none" ? "" : v })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No timeframe</SelectItem>
                  {TIMEFRAMES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGoalDialog(null)}>
              Cancel
            </Button>
            <Button onClick={() => saveGoal.mutate()} disabled={saveGoal.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function round(n: number) {
  return Math.round(n * 10) / 10;
}
